import { CouponIcon, EditNoteIcon, TrashIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import { isPartnerPurchase, SecondaryButton } from "common";
import React from "react";
import { useHistory } from "react-router-dom";
import { PaymentMethod, Purchase, PurchaseItem, PurchaseItemObject } from "types";
import { useVisibility } from "../../hooks/useVisibility";
import {
    AFFILIATE_LINE_ITEM_CODES,
    getCodes,
    getCouponCodes,
    getLineItemsByPaymentMethods,
    getMembershipCodes,
} from "../../models/LineItem";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { PromptModal } from "../Common/PromptModal";
import { EditPurchaseCodesModal } from "../PurchaseModification/EditPurchaseCodesModal";
import { EditPurchaseTaxesAndFeesModal } from "../PurchaseModification/EditPurchaseTaxesAndFeesModal";
import { usePurchaseResponseStore } from "./store/usePurchaseResponseStore";
import { isMistPurchase } from "@/models/CreatePurchaseState";
import { getTaxFeeLineItems } from "@/models/PurchaseItem";
import { canModifyTaxesAndFees } from "@/stores/globalStore";

interface PurchaseSummaryModifyOptionsProps {
    item: PurchaseItem;
    isActive?: boolean;
    itemIndex: number;
    setDisableEditButton: (value: boolean) => void;
}

export const PurchaseSummaryModifyOptions = ({
    item,
    isActive,
    itemIndex,
    setDisableEditButton,
}: PurchaseSummaryModifyOptionsProps) => {
    const history = useHistory();
    const shouldAllowTaxEdit = canModifyTaxesAndFees();

    const [isModifyTaxesOpen, { show: showModifyTaxes, hide: hideModifyTaxes }] = useVisibility(false);
    const [isModifyCodesOpen, { show: showModifyCodes, hide: hideModifyCodes }] = useVisibility(false);
    const [isRemoveReservationOpen, { show: showRemoveReservation, hide: hideRemoveReservation }] =
        useVisibility(false);

    const purchase = usePurchaseResponseStore((state) => state.purchase as Purchase);

    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const setActiveItemIndex = useCreatePurchaseStore((state) => state.actions.setActiveItemIndex);
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);
    const resetState = useCreatePurchaseStore((state) => state.actions.resetState);
    const removeCachedResponseItem = usePurchaseResponseStore((state) => state.actions.removeItem);
    const isPartnerBooking = purchase && isPartnerPurchase(purchase);

    const isVariableDemographicsPackage = useCreatePurchaseStore((state) => state.isVariableDemographicsPackage());
    const isGiftItem = item?.object === PurchaseItemObject.GIFT;
    const isMembershipItem = item.object === PurchaseItemObject.MEMBERSHIP;
    const taxFeesLineItems = getTaxFeeLineItems(item);

    // Here`s all code types that we can modify in Codes Modify modal, item don`t have any code we shouldn`t show modify Codes button
    const editCodesModalLineItems = [
        ...getCodes(AFFILIATE_LINE_ITEM_CODES, item.lineItems),
        ...getLineItemsByPaymentMethods([PaymentMethod.AFFILIATE_DEPOSIT], purchase.lineItems),
        ...getCouponCodes(item.lineItems, purchase.lineItems),
        ...getMembershipCodes(item.lineItems),
        ...getLineItemsByPaymentMethods([PaymentMethod.STORE_CREDIT, PaymentMethod.GIFT], purchase.lineItems),
    ];

    const handleTrashConfirm = () => {
        if (purchase.items.length === 1) {
            // There is only one item, so just start over completely
            resetState();
            history.push("/cart/init");
        } else {
            // MIST: Just remove this item and continue working on the purchase

            // New indexes to remove selected item, not just the active one
            const removeItemIndex = isActive ? activeItemIndex : itemIndex ?? activeItemIndex;
            const nextActiveItemIndex = activeItemIndex > 0 ? activeItemIndex - 1 : activeItemIndex;
            setPurchase((draft) => {
                draft.items.splice(removeItemIndex, 1);
                setActiveItemIndex(nextActiveItemIndex);
                draft.paymentReminders = []; // Resetting payment reminders since due amount
                useCreatePurchaseStore.setState({
                    step: isMistPurchase(draft) ? CreatePurchaseStep.SELECT_DATE : CreatePurchaseStep.CHECKOUT,
                });
            });

            removeCachedResponseItem(removeItemIndex);
        }

        hideRemoveReservation();
        setDisableEditButton(true);
    };

    const canEditTaxesAndFees =
        !isGiftItem && taxFeesLineItems?.length > 0 && !isMembershipItem && !isPartnerBooking && shouldAllowTaxEdit;

    return (
        <div className="space-x-1">
            {canEditTaxesAndFees && (
                <Tooltip content="Modify Taxes & Fees">
                    <SecondaryButton
                        size="small"
                        data-testid="purchase-item-summary-modify-taxes-fees-button"
                        onClick={showModifyTaxes}
                    >
                        <EditNoteIcon />
                    </SecondaryButton>
                </Tooltip>
            )}

            {!isGiftItem && editCodesModalLineItems.length > 0 && (
                <SecondaryButton
                    data-testid="purchase-item-summary-modify-codes"
                    size="small"
                    disabled={editCodesModalLineItems.length === 0}
                    onClick={showModifyCodes}
                >
                    <CouponIcon />
                </SecondaryButton>
            )}

            {isVariableDemographicsPackage ? null : (
                <Tooltip content="Delete">
                    <SecondaryButton
                        size="small"
                        data-testid="purchase-item-summary-remove-button"
                        onClick={showRemoveReservation}
                    >
                        <TrashIcon />
                    </SecondaryButton>
                </Tooltip>
            )}

            <PromptModal
                title="Remove Reservation?"
                isOpen={isRemoveReservationOpen}
                onConfirm={handleTrashConfirm}
                onClose={hideRemoveReservation}
            >
                Are you sure you want to remove this reservation from this purchase?
            </PromptModal>

            <EditPurchaseCodesModal
                purchase={purchase}
                item={item}
                isOpen={isModifyCodesOpen}
                onClose={hideModifyCodes}
            />

            <EditPurchaseTaxesAndFeesModal
                purchase={purchase}
                item={item}
                isOpen={isModifyTaxesOpen}
                onClose={hideModifyTaxes}
            />
        </div>
    );
};
